import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ColumnDef } from "@tanstack/react-table";
import { PartnerContactColumn, PartnerContactData } from "./types";
import { PartnerData, PartnerStatus } from "../types";
import {
  partnerStatusColors,
  getPartnerContactFullName,
  partnerContactColumnKeys
} from "./helpers";
import { getEnumTKey } from "@/utils/strings";
import { createEnumSortingFn } from "@/utils/enums";
import { simpleDate, toCalendarDate } from "@/utils/datetime";
import { StatusBadge } from "@/components/ui/status-badge";
import TableNameCell from "@/components/controls/TableNameCell";
import PartnerLinkCell from "./components/PartnerLinkCell";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { ColumnData } from "@/entities/dataTables/types";
import { mapSelectedCustomColumnsToTableColumns } from "@/entities/dataTables/helpers";
import { APIFieldItem } from "@/pages/settings/types";
import { getCountryDisplayName } from "@/utils/countries";
import { getCurrentLocale } from "@/utils/locale";

// Derived from the column keys, not from the enum: the enum also carries the
// partner FK filter key, which has no hardcoded ColumnDef to render.
const HARDCODED_PARTNER_CONTACT_COLUMN_IDS = new Set<string>(
  partnerContactColumnKeys
);

interface UsePartnerContactColumnsArgs {
  partnersDataMap: Record<string, PartnerData>;
  selectedColumns: ColumnData[];
  customFields: APIFieldItem[];
  isFieldVisible: (key: string) => boolean;
  fieldsLoaded: boolean;
}

export const usePartnerContactColumns = ({
  partnersDataMap,
  selectedColumns,
  customFields,
  isFieldVisible,
  fieldsLoaded
}: UsePartnerContactColumnsArgs) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const columnDefs: ColumnDef<PartnerContactData, any>[] = [
      {
        accessorKey: PartnerContactColumn.FULL_NAME,
        accessorFn: (row) => getPartnerContactFullName(row),
        header: t(`partnercontacts.${PartnerContactColumn.FULL_NAME}`),
        cell: (info) => (
          <TableNameCell info={info} entityType={EntityType.PARTNER_CONTACT} />
        )
      },
      {
        accessorKey: PartnerContactColumn.PARTNER_NAME,
        header: t(`partnercontacts.${PartnerContactColumn.PARTNER_NAME}`),
        cell: (info) => (
          <PartnerLinkCell
            partnerId={info.row.original.partner}
            // Prefer the async partners lookup, but fall back to the linked
            // partnerName the row was sorted and grouped on so a not-yet-loaded
            // partner still renders.
            partnerName={
              partnersDataMap[info.row.original.partner ?? ""]?.name ||
              (info.getValue() as string | null)
            }
          />
        )
      },
      {
        accessorKey: PartnerContactColumn.STATUS,
        header: t(`partnercontacts.${PartnerContactColumn.STATUS}`),
        sortingFn: createEnumSortingFn(PartnerStatus),
        minSize: 200,
        cell: (info) => {
          const statusKey = info.getValue() as PartnerStatus | null;
          if (!statusKey) {
            return t("tables.notAvailable");
          }
          const valueKey = getEnumTKey(
            "partnercontacts",
            "statusValues",
            PartnerStatus,
            statusKey
          );
          return (
            <StatusBadge statusColor={partnerStatusColors[statusKey]}>
              {t(valueKey)}
            </StatusBadge>
          );
        }
      },
      {
        accessorKey: PartnerContactColumn.JOB_TITLE,
        header: t(`partnercontacts.${PartnerContactColumn.JOB_TITLE}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: PartnerContactColumn.EMAILS,
        header: t(`partnercontacts.${PartnerContactColumn.EMAILS}`),
        cell: (info) => {
          const emails = info.getValue() as string[] | null;
          return emails && emails.length > 0
            ? emails.join(", ")
            : t("tables.notAvailable");
        }
      },
      {
        accessorKey: PartnerContactColumn.WORK_PHONE,
        header: t(`partnercontacts.${PartnerContactColumn.WORK_PHONE}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: PartnerContactColumn.MOBILE_PHONE,
        header: t(`partnercontacts.${PartnerContactColumn.MOBILE_PHONE}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: PartnerContactColumn.COUNTRY,
        header: t(`partnercontacts.${PartnerContactColumn.COUNTRY}`),
        cell: (info) => {
          const country = info.getValue() as string | null;
          return country
            ? getCountryDisplayName(country, getCurrentLocale())
            : t("tables.notAvailable");
        }
      },
      {
        accessorKey: PartnerContactColumn.STATE_REGION,
        header: t(`partnercontacts.${PartnerContactColumn.STATE_REGION}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: PartnerContactColumn.CITY,
        header: t(`partnercontacts.${PartnerContactColumn.CITY}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: PartnerContactColumn.POSTAL_CODE,
        header: t(`partnercontacts.${PartnerContactColumn.POSTAL_CODE}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: PartnerContactColumn.ADDRESS,
        header: t(`partnercontacts.${PartnerContactColumn.ADDRESS}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: PartnerContactColumn.CREATED_AT,
        header: t(`tables.${PartnerContactColumn.CREATED_AT}`),
        sortingFn: "datetime",
        meta: { groupByMonth: true, localDay: true },
        cell: (info) =>
          info.getValue()
            ? simpleDate(toCalendarDate(info.getValue() as string), "short")
            : t("tables.notAvailable")
      },
      ...mapSelectedCustomColumnsToTableColumns(
        selectedColumns,
        customFields,
        t,
        HARDCODED_PARTNER_CONTACT_COLUMN_IDS
      )
    ];
    return !fieldsLoaded
      ? columnDefs
      : columnDefs.filter((col) => {
          const key = (
            "accessorKey" in col ? col.accessorKey : col.id
          ) as string;
          return isFieldVisible(key);
        });
  }, [
    partnersDataMap,
    selectedColumns,
    customFields,
    isFieldVisible,
    fieldsLoaded,
    t
  ]);
};
